import React, { useEffect, useRef, useMemo , useState } from "react";
import Layout from "../Components/Layout";
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Card, 
  CardContent, 
  Button, 
  Chip,
  Grid 
} from "@mui/material";
import { QuestionAnswer as QuestionIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getUserPackage } from "../redux/actions/userActions";
import { useNavigate } from "react-router-dom";
// import LinearLoading from '../Components/reuse/LinearLoading';
import useCallOnce from "../hooks/useCallOnce";

function Packages({ selectedUserId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dialogContainerRef = useRef(null);
  const { data: userPackage, loading } = useSelector((s) => s.userPackage);
  

  // Local state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Extract package fields
  const pkg = useMemo(() => {
    const payload = userPackage && (userPackage.data ?? userPackage);
    return payload?.package || payload?.data?.package || payload;
  }, [userPackage]);

  // Load user package for the selected user
  useCallOnce("user/getUserPackage", () => {
    if (selectedUserId) dispatch(getUserPackage(selectedUserId));
  }, [dispatch, selectedUserId]);

  // Refetch on demand (not paginated for now)
  const handleRefresh = () => {
    if (selectedUserId) dispatch(getUserPackage(selectedUserId));
  };

  // Navigate to package logs
  const handleViewPackageLogs = () => {
    if (selectedUserId) {
      navigate('/package-logs', { state: { userId: selectedUserId } });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get type color for chip
  const getTypeColor = (type) => {
    switch (type) {
      case "order":
        return "primary";
      case "product":
        return "success";
      case "support":
        return "warning";
      default:
        return "default";
    }
  };

  const formatMoney = (v) => (v === undefined || v === null ? '—' : `$${Number(v).toFixed(2)}`);
  const formatBool = (v) => (v ? 'Active' : 'Inactive');

  return (
    // <Layout>
     <Box ref={dialogContainerRef} sx={{ p: { xs: 2, sm: 4 }, width: '100%', minWidth: 0, mt: 0 }}>
    
      {/* Header Section with Title and Description */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* Left Side - Title and Description */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              // mb: 1,
              // fontSize: { xs: "1.5rem", sm: "2rem" },
              color: "#111827",
            }}
          >
            Packages
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
      </Box>

      {/* Package Cards Container */}
      <Box sx={{ width: "100%" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ color: "#6b7280" }}>
              Loading packages...
            </Typography>
          </Box>
        ) : pkg ? (
          // Package Card
          
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl:3 }} >
              <Card
                onClick={handleViewPackageLogs}
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  border: "1px solid #e5e7eb",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                    transform: "translateY(-2px)",
                    borderColor: "#d1d5db",
                  },
                }}
              >
                <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  {/* Plan Title */}
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.5rem",
                      color: "#111827",
                      mb: 1,
                      textAlign: "center",
                    }}
                  >
                    {pkg?.name ?? 'Package'}
                  </Typography>

                  {/* Price */}
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: "2.25rem",
                      color: "#111827",
                      textAlign: "center",
                      mb: 0.5,
                    }}
                  >
                    {pkg?.price !== undefined ? `$${pkg.price}` : '$0'}
                  </Typography>

                  {/* Billing Cycle */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#6b7280",
                      textAlign: "center",
                      mb: 3,
                      fontSize: "0.875rem",
                    }}
                  >
                    per {pkg?.cycle === 'monthly' ? 'month' : 'year'}
                  </Typography>

                  {/* Divider Line */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      backgroundColor: "#e5e7eb",
                      mb: 3,
                    }}
                  />

                  {/* Features */}
                  <Box sx={{ mb: 4, flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.875rem" }}>
                        Calls Included:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
                        {pkg?.calls_included ?? '—'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.875rem" }}>
                        Cost per Call:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
                        {pkg?.cost_per_call !== undefined ? `$${pkg.cost_per_call}` : '—'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.875rem" }}>
                        Status:
                      </Typography>
                      <Chip
                        label={pkg?.is_active ? 'Active' : 'Inactive'}
                        color={pkg?.is_active ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Grid>
            </Grid>
        
        ) : (
          // Show empty state when no data
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
            }}
          >
            <QuestionIcon
              sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }}
            />
            <Typography
              variant="body1"
              sx={{ color: "#6b7280", mb: 1 }}
            >
              No package data found
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af" }}>
              Try reloading or selecting a different user
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
    // </Layout>
  );
}

export default Packages;
